import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp, Timestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import {
  FiArrowLeft, FiChevronLeft, FiChevronRight, FiCheck,
  FiChevronDown, FiChevronUp, FiBarChart2, FiList
} from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const getColorForHitValue = (val) => {
  const v = val?.toString().toUpperCase();
  if (v === '11' || v === 'X') return '#4CAF50'; // Zöld
  if (v === '10' || v === '9') return '#FFD700'; // Arany
  if (v === '8' || v === '7') return '#F44336';  // Piros
  if (v === '6' || v === '5') return '#1976D2';  // Kék
  if (v === 'M' || v === '0') return '#9E9E9E';  // Szürke
  return '#e0e0e0';
};

const bowShortLabels = {
  '3d_hdh_trrb': 'TRRB',
  '3d_hdh_bb': 'BB',
  '3d_hdh_cu': 'CU',
  '3d_hdh_hu': 'HU',
  '3d_hdh_pbhb': 'PBHB',
  '3d_hdh_trlb': 'TRLB',
  '3d_hdh_ol': 'OL',
  '3d_hdh_crb': 'CRB',
  '3d_hdh_bg': 'BG',
  '3d_hdh_cb': 'CB'
};

const Workout = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [workout, setWorkout] = useState(null);
  const [currentTarget, setCurrentTarget] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [showHistoryList, setShowHistoryList] = useState(false);
  const [isAverageInPercentage, setIsAverageInPercentage] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const authorUid = searchParams.get('authorUid');
  const isViewShared = searchParams.get('viewShared') === 'true';
  const [isReadOnly, setIsReadOnly] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 1. Auth és Workout betöltése
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Ha megosztott edzés nézet van (sessionStorage-ből)
      if (isViewShared) {
        try {
          const cachedShared = sessionStorage.getItem('viewing_shared_workout');
          if (cachedShared) {
            const parsed = JSON.parse(cachedShared);
            if (parsed.type === 'group' || parsed.type === 'rival') {
              alert("Csoportos és párbaj edzések részletei jelenleg csak saját nézetben érhetők el.");
              navigate('/kozosseg');
              return;
            }
            setWorkout(parsed);
            setCurrentTarget(parsed.currentSeries || 1);
            setIsReadOnly(true);
            sessionStorage.removeItem('viewing_shared_workout');
            return;
          }
        } catch (e) {}
      }

      if (authorUid) {
        try {
          const readOnlyMode = Boolean(!user || authorUid !== user.uid);
          setIsReadOnly(readOnlyMode);

          const docRef = doc(db, "users", authorUid, "workouts", id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.type === 'group' || data.type === 'rival') {
              alert("Csoportos és párbaj edzések részletei jelenleg csak saját nézetben érhetők el.");
              navigate('/kozosseg');
              return;
            }
            setWorkout({ id: docSnap.id, ...data });
            setCurrentTarget(data.currentSeries || 1);
            return;
          } else {
            alert("A megosztott edzés nem található!");
            navigate('/kozosseg');
            return;
          }
        } catch (e) {
          console.error("Hiba a megosztott edzés betöltésekor:", e);
        }
      }

      // LocalStorage aktív edzés betöltése
      const cached = localStorage.getItem(`active_workout_${id}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          setWorkout(parsed);
          setCurrentTarget(parsed.currentSeries || 1);
          return;
        } catch (e) {}
      }

      // Firestore lekérés ha a localstorage-ban nem volt
      if (user) {
        try {
          const docRef = doc(db, "users", user.uid, "workouts", id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setWorkout({ id: docSnap.id, ...data });
            setCurrentTarget(data.currentSeries || 1);
          } else {
            alert("Az edzés nem található!");
            navigate('/edzeseim');
          }
        } catch (e) {
          console.error("Hiba az edzés betöltésekor:", e);
        }
      }
    });
    return () => unsubscribe();
  }, [id, authorUid, navigate]);

  // Mentés LocalStorage-ba minden módosításkor
  useEffect(() => {
    if (workout) {
      localStorage.setItem(`active_workout_${id}`, JSON.stringify(workout));
    }
  }, [workout, id]);

  if (!workout) {
    return <div style={{ textAlign: 'center', padding: '50px', color: '#b30000', fontWeight: 'bold' }}>Edzés beírólap betöltése... 🏹</div>;
  }

  const possibleValues = workout.possibleHitValues || ['0', '5', '8', '10', '11', 'X'];
  const shotsPerT = Number(workout.shotsPerTarget) || 3;
  const targetCount = Number(workout.targetCount) || 20;

  // Gombok rendezése CSÖKKENŐ sorrendben (X, 11, 10, 9... 0)
  const sortedPossibleValues = [...possibleValues].sort((a, b) => {
    const numA = a === 'X' ? 999 : (a === 'M' ? -1 : parseInt(a) || 0);
    const numB = b === 'X' ? 999 : (b === 'M' ? -1 : parseInt(b) || 0);
    return numB - numA;
  });

  // Aktuális célállomás lövései
  const currentTargetShots = (workout.shots || []).filter(s => Number(s.target) === currentTarget);

  // Összpontszám és átlag számítás
  const totalScore = (workout.shots || []).reduce((acc, s) => acc + (Number(s.sum) || 0), 0);
  const totalShotsCount = (workout.shots || []).filter(s => s.value !== undefined && s.value !== '').length;
  const averageScore = totalShotsCount > 0 ? (totalScore / totalShotsCount).toFixed(2) : '0.00';

  // Zónatalálatok összeszámolása
  const zoneCounts = {};
  (workout.shots || []).forEach(s => {
    if (s.value) {
      const v = s.value.toUpperCase();
      zoneCounts[v] = (zoneCounts[v] || 0) + 1;
    }
  });

  // Grafikon adatok előkészítése (Átlag fejlődés)
  const chartData = [];
  let runningSum = 0;
  let shotIndex = 0;
  (workout.shots || []).forEach((s) => {
    if (s.value) {
      shotIndex++;
      runningSum += (Number(s.sum) || 0);
      chartData.push({
        shot: shotIndex,
        average: parseFloat((runningSum / shotIndex).toFixed(2))
      });
    }
  });

  // Dinamikus X érték és referencia max számítás
  const numericValues = possibleValues.map(v => parseInt(v)).filter(v => !isNaN(v));
  const maxNumericValue = numericValues.length > 0 ? Math.max(...numericValues) : 10;
  const referenceMaxScore = maxNumericValue > 10 ? 10 : Math.max(1, maxNumericValue);

  const expectedMaxSoFar = totalShotsCount * referenceMaxScore;
  const scoreDifference = totalScore - expectedMaxSoFar;
  const formattedDifference = totalShotsCount > 0 && scoreDifference !== 0
    ? ` (${scoreDifference > 0 ? '+' : ''}${scoreDifference})`
    : '';

  const formattedAverageDisplay = isAverageInPercentage
    ? `${((Number(averageScore) / referenceMaxScore) * 100).toFixed(1)}%`
    : averageScore;

  // --- PONTSZÁM RÖGZÍTÉSE ---
  const handleRecordHitValue = (val) => {
    if (isReadOnly) return;
    if (workout.status === 'completed' || workout.status === 'finished') {
      alert("Ez az edzés már le van zárva.");
      return;
    }

    const valNum = (val === 'X') ? maxNumericValue : (val === 'M' ? 0 : (parseInt(val) || 0));
    const updatedShots = [...(workout.shots || [])];

    let filled = false;
    for (let sIdx = 1; sIdx <= shotsPerT; sIdx++) {
      const existingIdx = updatedShots.findIndex(s => Number(s.target) === currentTarget && Number(s.position) === sIdx);

      if (existingIdx !== -1) {
        if (!updatedShots[existingIdx].value) {
          updatedShots[existingIdx] = { ...updatedShots[existingIdx], value: val, sum: valNum };
          filled = true;
          break;
        }
      } else {
        updatedShots.push({ target: currentTarget, position: sIdx.toString(), value: val, sum: valNum });
        filled = true;
        break;
      }
    }

    if (!filled) {
      if (currentTarget < targetCount) {
        setCurrentTarget(prev => prev + 1);
      }
      return;
    }

    const nextTotalScore = updatedShots.reduce((acc, s) => acc + (Number(s.sum) || 0), 0);
    const nextTotalCount = updatedShots.filter(s => s.value !== undefined && s.value !== '').length;
    const nextAvg = nextTotalCount > 0 ? Number((nextTotalScore / nextTotalCount).toFixed(2)) : 0;

    const updatedWorkout = {
      ...workout,
      shots: updatedShots,
      totalScore: nextTotalScore,
      totalArrowsShot: nextTotalCount,
      averageArrowScore: nextAvg,
      lastUpdated: new Date().toISOString()
    };

    setWorkout(updatedWorkout);

    // Automatukus léptetés ha betelt a cél
    const currentTargetFilledCount = updatedShots.filter(s => Number(s.target) === currentTarget && s.value !== '').length;
    if (currentTargetFilledCount >= shotsPerT && currentTarget < targetCount) {
      setTimeout(() => setCurrentTarget(prev => prev + 1), 200);
    }
  };

  // --- LÖVÉS TÖRLÉSE / JAVÍTÁSA ---
  const handleClearShot = (pos) => {
    if (isReadOnly) return;
    const updatedShots = (workout.shots || []).map(s => {
      if (Number(s.target) === currentTarget && Number(s.position) === pos) {
        return { ...s, value: '', sum: 0 };
      }
      return s;
    });

    const nextTotalScore = updatedShots.reduce((acc, s) => acc + (Number(s.sum) || 0), 0);
    const nextTotalCount = updatedShots.filter(s => s.value !== undefined && s.value !== '').length;
    const nextAvg = nextTotalCount > 0 ? Number((nextTotalScore / nextTotalCount).toFixed(2)) : 0;

    setWorkout({
      ...workout,
      shots: updatedShots,
      totalScore: nextTotalScore,
      totalArrowsShot: nextTotalCount,
      averageArrowScore: nextAvg
    });
  };

  // --- KÖVETKEZŐ CÉLÁLLOMÁSRA LÉPÉS VALIDÁCIÓVAL ---
  const handleNextTarget = () => {
    const filledCount = currentTargetShots.filter(s => s.value !== undefined && s.value !== '').length;
    if (filledCount < shotsPerT) {
      alert("Kérjük, rögzítsd az aktuális célállomás összes lövését a továbblépés előtt!");
      return;
    }
    if (currentTarget < targetCount) {
      setCurrentTarget(prev => prev + 1);
    }
  };

  // --- EDZÉS BEFEJEZÉSE ÉS KÖZVETLEN MENTÉSE A FIRESTORE FELHŐBE (AZ APP KOTLIN TYPE-JAIVAL 100% SZINKRONBAN) ---
  const handleFinishWorkout = async () => {
    if (!currentUser) {
      alert("Az edzés elmentéséhez be kell jelentkezned!");
      return;
    }

    setIsSaving(true);
    try {
      const filledShotsCount = (workout.shots || []).filter(s => s.value !== undefined && s.value !== '').length;

      // Dátum konvertálása valódi Firebase Timestamp objektummá (hogy a Kotlin doc.toObject ne akadjon ki)
      let createdAtTimestamp = serverTimestamp();
      if (workout.createdAt) {
        if (typeof workout.createdAt === 'string') {
          try {
            createdAtTimestamp = Timestamp.fromDate(new Date(workout.createdAt));
          } catch (e) {
            createdAtTimestamp = serverTimestamp();
          }
        } else if (workout.createdAt.seconds) {
          createdAtTimestamp = new Timestamp(workout.createdAt.seconds, workout.createdAt.nanoseconds || 0);
        }
      }

      // Lövések előkészítése stability: 0 mezővel
      const cleanShots = (workout.shots || []).map(s => ({
        value: (s.value || '').toString(),
        target: Number(s.target) || 1,
        sum: Number(s.sum) || 0,
        position: (s.position || '1').toString(),
        stability: Number(s.stability) || 0
      }));

      const finalWorkoutObj = {
        id: id,
        userId: currentUser.uid,
        rivalId: workout.rivalId || '',
        distance: Number(workout.distance) || 0,
        createdAt: createdAtTimestamp,
        lastUpdated: serverTimestamp(),
        lastUpdatedSync: serverTimestamp(),
        workoutName: workout.workoutName || 'Edzés',
        bowCategory: workout.bowCategory || '',
        targetFace: workout.targetFace || '',
        targetCount: Number(workout.targetCount) || 20,
        shotsPerTarget: Number(workout.shotsPerTarget) || 3,
        possibleHitValues: Array.isArray(workout.possibleHitValues) ? workout.possibleHitValues : ['0', '5', '8', '10', '11', 'X'],
        shots: cleanShots,
        status: 'completed',
        totalScore: Number(totalScore),
        averageArrowScore: Number(parseFloat(averageScore)),
        totalArrowsShot: Number(filledShotsCount),
        customNote: workout.customNote || '',
        currentSeries: Number(workout.currentSeries) || 1,
        type: workout.type || 'solo',
        shared: false,
        participants: Array.isArray(workout.participants) ? workout.participants : [],
        participantUids: Array.isArray(workout.participantUids) ? workout.participantUids : [],
        isSystemPost: false,
        likes: Array.isArray(workout.likes) ? workout.likes : [],
        congrats: Array.isArray(workout.congrats) ? workout.congrats : [],
        wow: Array.isArray(workout.wow) ? workout.wow : [],
        go: Array.isArray(workout.go) ? workout.go : []
      };

      // 1. KÖZVETLEN FIRESTORE MENTÉS A USERS/{UID}/MYWORKOUTS GYŰJTEMÉNYBE (AZ APP PONTOS ELEMÉBE)
      const docRef = doc(db, "users", currentUser.uid, "myworkouts", id);
      await setDoc(docRef, finalWorkoutObj, { merge: true });

      // 2. PROFIL TOTAL SHOTS SZÁMLÁLÓ NÖVELÉSE A FELHŐBEN
      if (filledShotsCount > 0) {
        try {
          const userRef = doc(db, "users", currentUser.uid);
          await updateDoc(userRef, {
            totalShots: increment(filledShotsCount)
          });
        } catch (e) {
          console.warn("Profil számláló frissítési hiba:", e);
        }
      }

      // 3. ATMENETI AKTÍV WORKOUT RÖGZÍTÉS LEÜRÍTÉSE LOCALSTORAGE-BÓL
      localStorage.removeItem(`active_workout_${id}`);

      alert("Edzés sikeresen befejezve és elmentve a felhőbe! 🏹🏆");
      navigate('/edzeseim');
    } catch (e) {
      console.error("Hiba az edzés lezárásakor:", e);
      alert("Hiba történt a mentés során.");
    } finally {
      setIsSaving(false);
    }
  };

  const bowCode = bowShortLabels[workout.bowCategory] || workout.bowCategory?.split('_').pop()?.toUpperCase() || 'TRRB';

  return (
    <div style={{ ...pageStyle, padding: isMobile ? '12px 10px 80px 10px' : '20px 20px 80px 20px' }} className="force-light-theme">
      {/* 1. KÁRTYA: cardWorkoutHeader (EDZÉS FEJLÉC ÉS SZÉRIA LÉPTETŐ) */}
      <div style={{ maxWidth: '550px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {isReadOnly && (
          <div style={{ backgroundColor: '#fff5f5', border: '1px solid #feb2b2', borderRadius: '8px', padding: '10px 15px', color: '#b30000', fontWeight: 'bold', fontSize: '0.9rem', textAlign: 'center' }}>
            🔍 Megosztott edzés (Read-Only nézet)
          </div>
        )}
        <div style={cardStyle}>
          {/* FELSŐ SOR: NÉV ÉS ÍJKATEGÓRIA KÓDJA */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button style={backButtonStyle} onClick={() => navigate((isViewShared || authorUid) ? '/kozosseg' : '/edzeseim')}>
                <FiArrowLeft size={20} />
              </button>
              <h2 style={{ margin: 0, color: '#333', fontSize: '1.3rem', fontWeight: 'bold' }}>
                {workout.workoutName}
              </h2>
            </div>
            <span style={bowBadgeStyle}>{bowCode}</span>
          </div>

          {/* ALSÓ SOR: CÉLÁLLOMÁS SZÉRIA LÉPTETŐ ÉS MENTÉS GOMB */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
            <button
              onClick={() => setCurrentTarget(prev => Math.max(1, prev - 1))}
              disabled={currentTarget <= 1}
              style={{ ...stepperButtonStyle, opacity: currentTarget <= 1 ? 0.3 : 1 }}
            >
              <FiChevronLeft size={22} />
            </button>

            <div style={{ fontSize: '1.2rem', fontWeight: '900', color: '#b30000' }}>
              {currentTarget} / {targetCount}
            </div>

            <button
              onClick={handleNextTarget}
              disabled={currentTarget >= targetCount}
              style={{
                ...stepperButtonStyle,
                opacity: (currentTarget >= targetCount || currentTargetShots.filter(s => s.value).length < shotsPerT) ? 0.3 : 1
              }}
              title="Következő célállomás"
            >
              <FiChevronRight size={22} />
            </button>

            {!isReadOnly && (
              <button onClick={handleFinishWorkout} disabled={isSaving} style={finishButtonStyle}>
                <FiCheck size={18} />
                <span>{isSaving ? '...' : 'Befejezés'}</span>
              </button>
            )}
          </div>
        </div>

        {/* 2. KÁRTYA: card_empty_shots (AKTUÁLIS CÉL LÖVÉSEINEK CHIPJEI) */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', overflowX: 'auto', padding: '6px 0' }}>
            {Array.from({ length: shotsPerT }).map((_, idx) => {
              const pos = idx + 1;
              const shotObj = currentTargetShots.find(s => Number(s.position) === pos);
              const val = shotObj?.value || '';
              const color = val ? getColorForHitValue(val) : '#fff';

              return (
                <div
                  key={pos}
                  onClick={() => !isReadOnly && val && handleClearShot(pos)}
                  style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: '12px',
                    border: val ? `2px solid ${color}` : '2px dashed #ccc',
                    backgroundColor: val ? color : '#fafafa',
                    color: val ? (val === '10' || val === '9' ? '#000' : '#fff') : '#888',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.3rem',
                    fontWeight: '900',
                    cursor: (!isReadOnly && val) ? 'pointer' : 'default',
                    boxShadow: val ? '0 3px 8px rgba(0,0,0,0.15)' : 'none',
                    transition: 'all 0.15s ease'
                  }}
                  title={(!isReadOnly && val) ? "Kattints a törléshez" : `Lövés ${pos}`}
                >
                  {val || `${pos}.`}
                </div>
              );
            })}
          </div>
        </div>

        {/* 3. KÁRTYA: cardHitValueButtons (PONTOZÓ GOMBOK CSÖKKENŐ SORRENDBEN) */}
        {!isReadOnly && (
          <div style={cardStyle}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
              {sortedPossibleValues.map(val => (
                <button
                  key={val}
                  type="button"
                  onClick={() => handleRecordHitValue(val)}
                  style={{
                    padding: '12px 18px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: '#b30000',
                    color: 'white',
                    fontSize: '1.2rem',
                    fontWeight: '900',
                    cursor: 'pointer',
                    boxShadow: '0 4px 10px rgba(179,0,0,0.25)',
                    minWidth: '55px',
                    transition: 'transform 0.1s ease'
                  }}
                  onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.92)'}
                  onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 4. KÁRTYA: cardStatisticsPanel (STATISZTIKAI PANEL ÉS ZÓNATALÁLATOK) */}
        <div style={cardStyle}>
          {/* FELSŐ SOR: ÖSSZPONTSZÁM (+/- ELTÉRÉRSEL) ÉS KATTINTHATÓ ÁTLAG % VÁLTÓ */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '12px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#2d3748' }}>
              Pont: <strong>{totalScore} p</strong>
              <span style={{ fontSize: '0.85rem', color: scoreDifference > 0 ? '#28a745' : (scoreDifference < 0 ? '#b30000' : '#888'), fontWeight: 'bold', marginLeft: '4px' }}>
                {formattedDifference}
              </span>
            </div>

            <button
              type="button"
              onClick={() => setIsAverageInPercentage(!isAverageInPercentage)}
              style={{
                padding: '6px 12px',
                backgroundColor: '#28a745',
                color: '#ffffff',
                border: 'none',
                borderRadius: '16px',
                fontWeight: 'bold',
                fontSize: '0.85rem',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(40,167,69,0.25)',
                transition: 'all 0.15s ease'
              }}
              title="Kattints a tizedes és százalékos kijelzés közötti váltáshoz"
            >
              Átlag: {formattedAverageDisplay}
            </button>
          </div>

          {/* ALSÓ SÁV: ZÓNATALÁLATOK (SZÍNES CHIP FELÜL, DARABSZÁM ALATTA) */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            {sortedPossibleValues.map(zone => {
              const count = zoneCounts[zone.toUpperCase()] || 0;
              const bg = getColorForHitValue(zone);
              const textColor = (zone === '10' || zone === '9') ? '#000' : '#fff';

              return (
                <div key={zone} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  {/* SZÍNES TALÁLATI CHIP */}
                  <div style={{
                    backgroundColor: bg,
                    color: textColor,
                    padding: '4px 10px',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    fontWeight: '900',
                    minWidth: '24px',
                    textAlign: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {zone}
                  </div>

                  {/* DARABSZÁM ALATTA (CSAK A SZÁM, DB NÉLKÜL) */}
                  <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#333' }}>
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 5. KÁRTYA: cardChart (ÁTLAG FEJLŐDÉS GRAFIKON) */}
        {chartData.length > 1 && (
          <div style={{ ...cardStyle, padding: '12px 10px' }}>
            <div style={{ width: '100%', height: '180px', marginLeft: '-5px', marginRight: '5px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 15, left: -10, bottom: 0 }}>
                  <XAxis dataKey="shot" stroke="#888" fontSize={10} />
                  <YAxis domain={['auto', 'auto']} stroke="#888" fontSize={10} width={32} />
                  <Tooltip formatter={(value) => [`${value} p/nyíl`, 'Átlag']} />
                  <Line type="monotone" dataKey="average" stroke="#28a745" strokeWidth={3} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* 6. KÁRTYA: cardHistoryList (LENYITHATÓ TALÁLATI LISTA NÉZET) */}
        <div style={cardStyle}>
          <div
            onClick={() => setShowHistoryList(!showHistoryList)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          >
            <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiList size={18} color="#b30000" />
              <span>Találati Lista Nézet ({targetCount} célállomás)</span>
            </div>
            {showHistoryList ? <FiChevronUp size={20} color="#888" /> : <FiChevronDown size={20} color="#888" />}
          </div>

          {showHistoryList && (() => {
            let runningSum = 0;

            return (
              <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Array.from({ length: targetCount }).map((_, idx) => {
                  const targetNum = idx + 1;
                  const shots = (workout.shots || []).filter(s => Number(s.target) === targetNum && s.value !== '');
                  const targetTotal = shots.reduce((acc, s) => acc + (Number(s.sum) || 0), 0);
                  runningSum += targetTotal;

                  return (
                    <div key={targetNum} style={historyRowStyle}>
                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#b30000', width: '40px', flexShrink: 0 }}>
                        #{targetNum}
                      </div>

                      <div style={{ flex: 1, display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {shots.map((s, sIdx) => {
                          const bg = getColorForHitValue(s.value);
                          return (
                            <span key={sIdx} style={{
                              backgroundColor: bg,
                              color: (s.value === '10' || s.value === '9') ? '#000' : '#fff',
                              padding: '2px 7px',
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              fontWeight: 'bold'
                            }}>
                              {s.value}
                            </span>
                          );
                        })}
                        {shots.length === 0 && (
                          <span style={{ fontSize: '0.8rem', color: '#ccc', fontStyle: 'italic' }}>Nincs lövés</span>
                        )}
                      </div>

                      <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#333', minWidth: '24px', textAlign: 'right' }}>
                          {targetTotal}
                        </span>
                        {shots.length > 0 && (
                          <span style={{ fontWeight: '900', fontSize: '0.9rem', color: '#b30000', minWidth: '30px', textAlign: 'right' }}>
                            {runningSum}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

      </div>
    </div>
  );
};

// --- STÍLUSOK ---
const pageStyle = { backgroundColor: '#f4f6f8', minHeight: '100vh', boxSizing: 'border-box' };
const backButtonStyle = { background: 'none', border: 'none', color: '#333', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' };
const finishButtonStyle = { padding: '8px 14px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 10px rgba(40,167,69,0.3)' };

const cardStyle = { backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #eee' };
const stepperButtonStyle = { width: '38px', height: '38px', borderRadius: '50%', border: '1px solid #ddd', backgroundColor: '#fff', color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const bowBadgeStyle = { backgroundColor: '#fff5f5', color: '#b30000', border: '1px solid #feb2b2', padding: '3px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold' };

const historyRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', backgroundColor: '#fafafa', border: '1px solid #eee' };

export default Workout;
